import { useCallback, useEffect, useState } from "react";
import * as HoverCard from "@radix-ui/react-hover-card";
import { Flex, Text, Button } from "@radix-ui/themes";
import { FaChevronDown, FaCircle } from "react-icons/fa";

import TypesData from "@/data/audiolab/types.json";
import { marketplaceTokens } from "@/lib/tokens";
import { cn } from "@/lib/utils";
import FilterCheckbox from "./shared/FilterCheckbox";
import FilterFooter from "./shared/FilterFooter";
import useFilterDropdown from "./shared/useFilterDropdown";
import { MarketplaceFilterKey, MarketplaceUpdates } from "../types";

type SoundkitTypesFilterProps = {
  parameter?: string;
  onSetFilter: (updates: MarketplaceUpdates) => void;
  onRemoveFilter: (param: MarketplaceFilterKey) => void;
};

export default function SoundkitTypesFilter({
  parameter,
  onSetFilter,
  onRemoveFilter,
}: SoundkitTypesFilterProps) {
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const restoreFromParameter = useCallback(() => {
    if (parameter) {
      setSelectedTypes(parameter.split(", "));
    } else {
      setSelectedTypes([]);
    }
  }, [parameter]);

  const {
    open,
    isPinned,
    handleOpenChange,
    handleTriggerClick,
    handleInteractOutside,
    close,
  } = useFilterDropdown({
    id: "soundkit-types",
    onRestore: restoreFromParameter,
  });

  useEffect(() => {
    restoreFromParameter();
  }, [parameter, restoreFromParameter]);

  function toggleType(typeValue: string) {
    const updated = [...selectedTypes];
    const index = updated.indexOf(typeValue);

    if (index >= 0) {
      updated.splice(index, 1);
    } else {
      updated.push(typeValue);
    }

    setSelectedTypes(updated);
  }

  function handleApply() {
    if (selectedTypes.length === 0) {
      onRemoveFilter("types");
    } else {
      onSetFilter({ types: selectedTypes.join(", ") });
    }
    close({ restore: false });
  }

  function handleReset() {
    setSelectedTypes([]);
    onRemoveFilter("types");
    close({ restore: false });
  }

  // Split types into two columns
  const midpoint = Math.ceil(TypesData.length / 2);
  const leftColumn = TypesData.slice(0, midpoint);
  const rightColumn = TypesData.slice(midpoint);

  const hasSelection = Boolean(parameter);

  return (
    <HoverCard.Root
      open={open}
      onOpenChange={handleOpenChange}
      openDelay={isPinned ? 0 : 200}
      closeDelay={isPinned ? 0 : 100}
    >
      <HoverCard.Trigger asChild>
        <Button
          onClick={handleTriggerClick}
          className={cn(
            "flex items-center gap-[8px] w-full text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2 focus-visible:ring-offset-black",
            marketplaceTokens.filterItem,
            hasSelection && "!text-brand-primary",
            {
              [marketplaceTokens.filterItemActive]: open,
              [marketplaceTokens.filterItemInactive]: !open,
            },
          )}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-pressed={isPinned}
        >
          {hasSelection && <FaCircle size={9} className="text-brand-primary" />}
          <Text className="flex-1">Types</Text>
          <FaChevronDown
            size={10}
            aria-hidden
            className={cn("transition-transform", {
              "-rotate-90": isPinned,
            })}
          />
        </Button>
      </HoverCard.Trigger>

      <HoverCard.Portal>
        <HoverCard.Content
          side="right"
          align="start"
          sideOffset={10}
          className="w-[350px] bg-black rounded shadow-[0px_10px_18px_-1px_rgba(5,12,43,0.14)] pt-4 px-4 pb-3 flex flex-col gap-4 z-50"
          onInteractOutside={handleInteractOutside}
        >
          <Text className="text-gray-500 text-[14px] font-normal">Types</Text>
          {/* Two-column grid */}
          <Flex className="gap-4">
            {/* Left Column */}
            <Flex direction="column" className="flex-1 gap-[2px]">
              {leftColumn.map((type) => {
                const isSelected = selectedTypes.includes(type.value);
                return (
                  <Flex
                    key={type.value}
                    onClick={() => toggleType(type.value)}
                    className="h-[34px] px-[8px] rounded flex items-center gap-[8px] cursor-pointer hover:bg-gray-800/50"
                  >
                    <FilterCheckbox checked={isSelected} />
                    <Text className="text-white text-[14px] font-semibold">
                      {type.title}
                    </Text>
                  </Flex>
                );
              })}
            </Flex>

            {/* Right Column */}
            <Flex direction="column" className="flex-1 gap-[2px]">
              {rightColumn.map((type) => {
                const isSelected = selectedTypes.includes(type.value);
                return (
                  <Flex
                    key={type.value}
                    onClick={() => toggleType(type.value)}
                    className="h-[34px] px-[8px] rounded flex items-center gap-[8px] cursor-pointer hover:bg-gray-800/50"
                  >
                    <FilterCheckbox checked={isSelected} />
                    <Text className="text-white text-[14px] font-semibold">
                      {type.title}
                    </Text>
                  </Flex>
                );
              })}
            </Flex>
          </Flex>

          {/* Footer */}
          <FilterFooter onReset={handleReset} onApply={handleApply} />
        </HoverCard.Content>
      </HoverCard.Portal>
    </HoverCard.Root>
  );
}
